/**
 * User Profile Service
 * Handles user profile and settings management
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Default profile data
 */
const DEFAULT_PROFILE = {
  businessName: '',
  businessAddress: '',
  defaultRate: 0,
  defaultCurrency: 'USD',
  taxRate: 0,
  notifications: {
    invoicePaid: true,
    invoiceOverdue: true,
    autoReminders: true,
    weeklySummary: false,
    reminderFrequency: 7,
  },
  createdAt: new Date(),
  updatedAt: new Date(),
};

/**
 * Get user profile
 */
export const getUserProfile = async (userId) => {
  // Return defaults if Firebase is not configured
  if (!db) {
    console.warn('Firebase not configured, using default profile');
    return DEFAULT_PROFILE;
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Return default profile if doesn't exist
      return DEFAULT_PROFILE;
    }

    return userDoc.data();
  } catch (error) {
    console.error('Error getting user profile:', error);
    // Return defaults on error instead of throwing
    return DEFAULT_PROFILE;
  }
};

/**
 * Create or update user profile
 */
export const saveUserProfile = async (userId, profileData) => {
  if (!db) {
    console.warn('Firebase not configured, cannot save profile');
    return { success: false, error: 'Firebase not configured' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    const data = {
      ...profileData,
      updatedAt: serverTimestamp(),
    };

    if (!userDoc.exists()) {
      // Create new profile
      data.createdAt = serverTimestamp();
      await setDoc(userRef, data);
    } else {
      // Update existing profile
      await updateDoc(userRef, data);
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw error;
  }
};

/**
 * Update profile settings
 */
export const updateProfileSettings = async (userId, settings) => {
  if (!db) {
    console.warn('Firebase not configured, cannot update settings');
    return { success: false, error: 'Firebase not configured' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      ...settings,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating profile settings:', error);
    throw error;
  }
};

/**
 * Update notification preferences
 */
export const updateNotificationPreferences = async (userId, notifications) => {
  if (!db) {
    console.warn('Firebase not configured, cannot update notifications');
    return { success: false, error: 'Firebase not configured' };
  }

  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      notifications,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating notification preferences:', error);
    throw error;
  }
};
